/**
 * Brand World → buyer media proforma (order-request / aggregator) uses flat
 * line totals from the invoice snapshot — not rate × timeline × seconds.
 */
import { formatHsnForDisplay } from "../../utils/formatHsnForDisplay";

export function isMediaBrandWorldAggregatorPI(orderSummary) {
    return Boolean(
      orderSummary &&
        orderSummary.IsMedia === true &&
        orderSummary.issuedByBrandWorld === true
    );
  }
  
  /** Ex-GST taxable amount for one product row (Trade Credits / BXI leg). */
  export function mediaBrandWorldProductLineTaxableExGst(item) {
    const qty = Number(item?.ProductQuantity) || 1;
    const isMultiplex = Boolean(item?.isMultiplexMedia || item?.IsMultiplexMedia);
    const fromCoins = Number(
      item?.totalMediaCoins ?? item?.TotalMediaCoins ?? item?.TotalMediaPrice,
    );
    if (isMultiplex && Number.isFinite(fromCoins) && fromCoins > 0) {
      return fromCoins;
    }
    const explicit =
      Number(item?.TotalPriceWithoutGST) || Number(item?.TotalMediaPrice);
    if (Number.isFinite(explicit) && explicit > 0) {
      return explicit;
    }
    const perUnit = Number(item?.PriceWithoutGST);
    if (Number.isFinite(perUnit) && perUnit > 0) return perUnit * qty;
    return (Number(item?.DiscountedPrice) || 0) * qty;
  }
  
  /** True when invoice should show IGST (GSTIN / state of buyer vs seller). */
  export function invoiceEffectiveIsIgst(orderSummary) {
    if (!orderSummary) return false;
    const buyerGstin = orderSummary?.BuyerDetails?.GSTIN || orderSummary?.BuyerDetails?.gstin;
    const sellerGstin = orderSummary?.SellerDetails?.GSTIN || orderSummary?.SellerDetails?.gstin;
    const buyerPrefix = String(buyerGstin || "").replace(/\s/g, "").slice(0, 2);
    const sellerPrefix = String(sellerGstin || "").replace(/\s/g, "").slice(0, 2);
    if (/^\d{2}$/.test(buyerPrefix) && /^\d{2}$/.test(sellerPrefix) && buyerPrefix !== "00" && sellerPrefix !== "00") {
      return buyerPrefix !== sellerPrefix;
    }
    const bw = Number(orderSummary?.BuyerDetails?.StateCode) || 0;
    const sl = Number(orderSummary?.SellerDetails?.StateCode) || 0;
    if (bw > 0 && sl > 0) return bw !== sl;
    return orderSummary.IsIGST === true;
  }
  
/**
 * Human-readable timeline for media PI tables (avoids "1 undefined").
 * @param {object} row — ProductData row
 * @param {{ isAggregatorPi?: boolean, column?: 'bought' | 'duration', parentRow?: object }} opts
 */

function isLegacySpotTimelinePlaceholder(row) {
  const unit = String(row?.Timeline || row?.timeline || '').trim();
  return /^spot$/i.test(unit) && Number(row?.TimelineToBought) === 1;
}

export function resolveMediaTimelineToBought(row, parentRow) {
  const skipLegacyInvoiceOne =
    isLegacySpotTimelinePlaceholder(row) ||
    isLegacySpotTimelinePlaceholder(parentRow);
  const candidates = [
    row?.timeLineToBought,
    row?.TimeLineToBought,
    row?.timelineToBought,
    skipLegacyInvoiceOne ? null : row?.TimelineToBought,
    row?.NumberOfDays,
    row?.numberOfDays,
    row?.mediaLineItems?.[0]?.timeLineToBought,
    row?.mediaLineItems?.[0]?.TimeLineToBought,
    row?.mediaLineItems?.[0]?.TimelineToBought,
    row?.mediaLineItems?.[0]?.NumberOfDays,
    parentRow?.timeLineToBought,
    parentRow?.TimeLineToBought,
    skipLegacyInvoiceOne ? null : parentRow?.TimelineToBought,
    parentRow?.NumberOfDays,
  ];
  let best = null;
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      if (best == null || n > best) best = n;
    }
  }
  return best;
}

export function resolveMediaTimelineUnit(row, parentRow) {
  const candidates = [
    row?.Timeline,
    row?.timeline,
    row?.mediaLineItems?.[0]?.Timeline,
    row?.mediaLineItems?.[0]?.timeline,
    parentRow?.Timeline,
    parentRow?.timeline,
  ];
  for (const raw of candidates) {
    if (raw == null) continue;
    const s = String(raw).trim();
    if (!s || /^spot$/i.test(s)) continue;
    return s;
  }
  return '';
}

/**
 * Media listing HSN lives on mediaVariation; older PIs may have HSN: 0.
 * Prefer any non-zero candidate on the invoice row, nested fields, or OR line.
 */
export function resolveMediaPiHsn(row, orderRequest) {
  const productId = String(row?.ProductId || row?.productId || "");
  const orProducts = Array.isArray(orderRequest?.productData)
    ? orderRequest.productData
    : [];
  const orLine =
    (productId &&
      orProducts.find((src) => {
        const srcId = String(src?.productId || src?.ProductId || "");
        return srcId && srcId === productId;
      })) ||
    null;
  const candidates = [
    row?.HSN,
    row?.hsn,
    row?.mediaVariation?.HSN,
    row?.mediaVariation?.hsn,
    row?.ProductsVariantions?.[0]?.HSN,
    row?.mediaLineItems?.[0]?.HSN,
    orLine?.hsn,
    orLine?.HSN,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const s = String(c).trim();
    if (!s || /^0+$/.test(s)) continue;
    return formatHsnForDisplay(s);
  }
  return "";
}

export function resolveMediaPiBoughtSeconds(row, orderSummary) {
  const orderTotalsSec = Number(
    orderSummary?.totals?.BoughtSeconds ?? orderSummary?.PITotals?.BoughtSeconds,
  );
  const lineSec = Number(row?.mediaLineItems?.[0]?.BoughtSeconds);
  const rowSec = Number(row?.BoughtSeconds);
  const rowTotalsSec = Number(row?.totals?.BoughtSeconds);

  if (isMediaBrandWorldAggregatorPI(orderSummary)) {
    if (Number.isFinite(orderTotalsSec) && orderTotalsSec > 0) return orderTotalsSec;
    if (Number.isFinite(lineSec) && lineSec > 0) return lineSec;
    if (Number.isFinite(rowTotalsSec) && rowTotalsSec > 0) return rowTotalsSec;
    if (Number.isFinite(rowSec) && rowSec > 0) return rowSec;
    return null;
  }

  if (Number.isFinite(lineSec) && lineSec > 0) return lineSec;
  if (Number.isFinite(rowSec) && rowSec > 0) return rowSec;
  if (Number.isFinite(rowTotalsSec) && rowTotalsSec > 0) return rowTotalsSec;
  if (Number.isFinite(orderTotalsSec) && orderTotalsSec > 0) return orderTotalsSec;
  return null;
}

export function formatMediaTimelineLabel(row, opts = {}) {
  const {
    isAggregatorPi = false,
    column = 'bought',
    parentRow = null,
  } = opts;
  const ptype = row?.ProductTypeId || '';
  const offline =
    ptype === 'MediaOffline' || ptype === 'News Papers / Magazines';

  if (column === 'duration') {
    if (isAggregatorPi) return '—';
    if (offline) {
      const unit = resolveMediaTimelineUnit(row, parentRow);
      if (unit) return `1 ${unit}`;
      return '—';
    }
    return '';
  }

  if (offline && !isAggregatorPi) return '';

  const n = resolveMediaTimelineToBought(row, parentRow);
  const unit = resolveMediaTimelineUnit(row, parentRow);
  const u = unit || 'Day';

  if (n != null) {
    const numStr = String(n);
    const pluralizedU =
      n !== 1 && !/s$/i.test(u) ? `${u}s` : u;
    return `${numStr} ${pluralizedU}`;
  }

  // Brand World media ready-stock PIs carry the booked duration (TimelineToBought /
  // NumberOfDays). When it cannot be resolved, show a neutral dash rather than the
  // misleading literal "Ready stock" in the timeline/duration column.
  if (isAggregatorPi) return '—';
  if (!unit) return '—';
  return u;
}
  
  /**
   * BXI commission line for aggregator media PI — uses persisted INR ex-GST + GST.
   * @param {object} orderSummary — invoice / order-summary payload
   * @param {{ name?: string }} opts
   */
  export function buildMediaBrandWorldCommissionCharge(orderSummary, opts = {}) {
    const name = opts.name || 'BXI Commission (INR)';
    const comm = Number(orderSummary?.aggregatorCommission ?? 0);
    const commGst = Number(orderSummary?.aggregatorCommissionGST ?? 0);
    let productBase = 0;
    (orderSummary?.ProductData || []).forEach((row) => {
      productBase += mediaBrandWorldProductLineTaxableExGst(row);
    });
    const pctFromOr = Number(orderSummary?.commission?.percentage);
    const commissionDisplayPct =
      pctFromOr > 0 && Number.isFinite(pctFromOr)
        ? pctFromOr
        : comm > 0 && productBase > 0
          ? (comm / productBase) * 100
          : 0;
    return {
      name,
      gst: 18,
      commission: commissionDisplayPct,
      hsn: '996211',
      taxableValue: comm,
      totalTaxAmount: commGst,
    };
  }
  
  function parseMoney(v) {
    if (v == null || v === '') return 0;
    const n = Number(String(v).replace(/,/g, '').trim());
    return Number.isFinite(n) ? n : 0;
  }
  
  /**
   * Packaging, logistics, and insurance charge lines (same fields as product ProformaInvoice).
   * @param {object} inv — Invoice / order-summary payload
   */
  export function buildInvoiceLogisticsAndIncidentalCharges(inv) {
    if (!inv) return [];
    const rows = [];
    const pack = inv.PackagingData;
    if (pack) {
      const pv = parseMoney(pack.packagingcosts);
      const pg = parseMoney(pack.packagingcostgstfee);
      if (pv > 0 || pg > 0) {
        rows.push({
          name: 'Packaging charges',
          gst: pg || 0,
          hsn: '998540',
          taxableValue: pv,
          totalTaxAmount: (pv * pg) / 100,
        });
      }
    }
    const tr = inv.Transportationdata;
    if (tr) {
      const fee = parseMoney(tr.transportationfee ?? tr.transportationFee);
      const gstFee = parseMoney(tr.gstFee ?? tr.gstfee);
      if (fee > 0 || gstFee > 0) {
        rows.push({
          name: 'Logistics charges',
          gst: gstFee || 0,
          hsn: '996519',
          taxableValue: fee,
          totalTaxAmount: (fee * gstFee) / 100,
        });
      }
    }
    const ins = parseMoney(inv.InsuranceCost);
    if (ins > 0) {
      rows.push({
        name: 'Insurance cost',
        gst: 18,
        hsn: '997135',
        taxableValue: ins,
        totalTaxAmount: (ins * 18) / 100,
      });
    }
    return rows;
  }

/**
 * Per-product additional cost rows for media PI (aligns with PI.jsx / invoice payloads).
 */
export function getMediaProductAdditionalCostRows(row, orderSummarySnapshot) {
  if (!row || typeof row !== 'object') return [];
  const rowLevelAdditional =
    row?.AdditionalCost?.AdditionCostArrayData ||
    row?.AdditionalCost?.AdditionCostArray ||
    row?.AdditionCost?.AdditionCostArrayData ||
    row?.AdditionCost?.AdditionCostArray ||
    row?.AdditionCostArrayData ||
    row?.AdditionCostArray;
  const rowLevelRows = Array.isArray(rowLevelAdditional)
    ? rowLevelAdditional
    : [];
  const snapshotLevelAdditional = orderSummarySnapshot?.AdditionalCost;
  const snapshotRows = Array.isArray(snapshotLevelAdditional)
    ? snapshotLevelAdditional
    : [];
  return rowLevelRows.length > 0 ? rowLevelRows : snapshotRows;
}
